/**
 * salesRegister.ts — Pure aggregation of a period's outward documents into a
 * chronological register: every sale invoice and credit note with its GST split
 * and totals net of returns. The sales-side counterpart to the Purchase
 * Register, and the document list a CA reconciles GSTR-1 against.
 *
 * Credit notes carry a −1 sign, so the register's totals are net (they match the
 * Sales report and GSTR-1 outward figures). Each row keeps its own document
 * values; the sign only affects the totals.
 *
 * Pure — the screen feeds it rows from the data source.
 */

export const CREDIT_NOTE = 'CREDIT_NOTE';

export function signOf(type: string): 1 | -1 {
  return type === CREDIT_NOTE ? -1 : 1;
}

export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

// ─── Inputs ──────────────────────────────────────────────────────────────────

export interface RegisterInvoiceInput {
  invoiceNumber: string;
  date: Date;
  customerName: string;
  gstin: string | null;
  type: string;
  taxable: number;
  cgst: number;
  sgst: number;
  igst: number;
  cess: number;
  totalTax: number;
  total: number;
  paymentStatus: string;
}

// ─── Output ──────────────────────────────────────────────────────────────────

export interface RegisterRow extends RegisterInvoiceInput {
  isCreditNote: boolean;
}

export interface RegisterTotals {
  taxable: number;
  cgst: number;
  sgst: number;
  igst: number;
  cess: number;
  totalTax: number;
  value: number;
  count: number;
}

export interface PaymentStatusSummary {
  status: string;
  count: number;
  value: number;
}

export interface SalesRegister {
  rows: RegisterRow[];
  /** Net of credit notes. */
  totals: RegisterTotals;
  saleCount: number;
  creditNoteCount: number;
  byPaymentStatus: PaymentStatusSummary[];
}

export function buildSalesRegister(invoices: RegisterInvoiceInput[]): SalesRegister {
  const rows: RegisterRow[] = invoices
    .map((i) => ({ ...i, isCreditNote: i.type === CREDIT_NOTE }))
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  const totals: RegisterTotals = { taxable: 0, cgst: 0, sgst: 0, igst: 0, cess: 0, totalTax: 0, value: 0, count: rows.length };
  let saleCount = 0;
  let creditNoteCount = 0;
  const payMap = new Map<string, { count: number; value: number }>();

  for (const r of rows) {
    const sign = signOf(r.type);
    totals.taxable += sign * r.taxable;
    totals.cgst += sign * r.cgst;
    totals.sgst += sign * r.sgst;
    totals.igst += sign * r.igst;
    totals.cess += sign * r.cess;
    totals.totalTax += sign * r.totalTax;
    totals.value += sign * r.total;

    if (r.isCreditNote) {
      creditNoteCount += 1;
    } else {
      saleCount += 1;
      // Payment-status split covers sale invoices only.
      const p = payMap.get(r.paymentStatus) ?? { count: 0, value: 0 };
      p.count += 1;
      p.value += r.total;
      payMap.set(r.paymentStatus, p);
    }
  }

  const byPaymentStatus: PaymentStatusSummary[] = [...payMap.entries()]
    .map(([status, v]) => ({ status, count: v.count, value: round2(v.value) }))
    .sort((a, b) => b.value - a.value);

  return {
    rows,
    totals: {
      taxable: round2(totals.taxable),
      cgst: round2(totals.cgst),
      sgst: round2(totals.sgst),
      igst: round2(totals.igst),
      cess: round2(totals.cess),
      totalTax: round2(totals.totalTax),
      value: round2(totals.value),
      count: totals.count,
    },
    saleCount,
    creditNoteCount,
    byPaymentStatus,
  };
}
